import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { MessageSquare, X } from "lucide-react";
import { api } from "../lib/api";
import CommentsSection from "./CommentsSection";

/**
 * CommentButton — a compact chat icon that shows the live comment count and
 * opens a modal thread when clicked. Used on news / milestone / launch cards
 * where a full CommentsSection would be too heavy.
 *
 * Props:
 *   targetKind: "news" | "launch" | "rocket" | "milestone"
 *   targetId: string
 *   title:     string   (shown in the modal header)
 *   size:      number   (icon size, default 13)
 *   testid?:   string
 */
export default function CommentButton({ targetKind, targetId, title, size = 13, testid }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get(`/comments/count`, { params: { target_kind: targetKind, target_id: targetId } });
      setCount(r.data?.count ?? 0);
    } catch (_) {}
  }, [targetKind, targetId]);

  useEffect(() => { loadCount(); }, [loadCount]);

  const onToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(o => !o);
  };

  const close = () => { setOpen(false); loadCount(); };

  return (
    <>
      <button
        onClick={onToggle}
        data-testid={testid || `comment-btn-${targetKind}-${targetId}`}
        aria-label="Open comments"
        className={`p-1.5 border transition-colors inline-flex items-center gap-1 ${
          count > 0
            ? "border-[color:var(--sat-cyan)] sat-cyan"
            : "border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] hover:sat-cyan hover:border-[color:var(--sat-cyan)]"
        }`}
      >
        <MessageSquare size={size} />
        {count !== null && count > 0 && (
          <span className="text-[10px] font-mono tabular-nums" data-testid="comment-btn-count">{count}</span>
        )}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          data-testid="comments-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] sat-box-glow">
              <div className="flex items-start justify-between gap-3 p-4 md:p-5 border-b border-[color:var(--sat-border-bright)]">
                <div className="flex-1 min-w-0">
                  <div className="sat-label sat-cyan">COMMS // {targetKind.toUpperCase()}</div>
                  <div className="font-display font-bold text-lg mt-1 truncate" data-testid="comments-modal-title">{title}</div>
                </div>
                <button onClick={close}
                        className="p-1.5 border border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] hover:border-[color:var(--sat-cyan)]"
                        data-testid="comments-modal-close" aria-label="Close">
                  <X size={14} />
                </button>
              </div>
              <div className="p-4 md:p-5">
                <CommentsSection targetKind={targetKind} targetId={targetId} variant="block" testid="comments-modal-section" />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
